(() => {
  const SELECTOR = '[data-ilamo-phase-one]';
  class IlamoPhaseOne {
    constructor(root) {
      if (!root || root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';
      this.root = root;
      this.slides = [...root.querySelectorAll('[data-ilamo-slide]')];
      this.pagination = [...root.querySelectorAll('[data-ilamo-go]')];
      this.current = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplayEnabled = root.dataset.autoplay === 'true' && this.slides.length > 1;
      this.autoplaySpeed = Number(root.dataset.autoplaySpeed) || 8000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.motionEnabled = root.dataset.motion !== 'false';
      this.timer = null;
      this.commitTimer = null;
      this.transitionTimer = null;
      this.transitioning = false;
      this.paused = false;
      this.pointerStart = null;
      this.cleanup = [];

      if (this.motionEnabled) root.classList.add('ilamo-motion-ready');
      if (this.reducedMotion) root.classList.add('ilamo-reduced-motion');

      this.bindSlider();
      this.bindRealHeader();
      this.prepareVideos();
      this.setAutoplayDuration();

      this.on(document, 'visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });

      window.setTimeout(() => {
        root.classList.add('is-ready');
        this.startAutoplay();
      }, 60);
    }

    on(target, event, handler, options) {
      if (!target) return;
      target.addEventListener(event, handler, options);
      this.cleanup.push(() => target.removeEventListener(event, handler, options));
    }

    setAutoplayDuration() {
      this.root.style.setProperty('--ilamo-autoplay-duration', `${this.autoplaySpeed}ms`);
    }

    bindRealHeader() {
      const update = () => document.body.classList.toggle('ilamo-header-scrolled', window.scrollY > 24);
      this.on(window, 'scroll', update, { passive: true });
      update();
    }

    bindSlider() {
      const slider = this.root.querySelector('[data-ilamo-slider]');
      const prev = this.root.querySelector('[data-ilamo-prev]');
      const next = this.root.querySelector('[data-ilamo-next]');

      this.pagination.forEach((button) => {
        this.on(button, 'click', () => {
          this.go(Number(button.dataset.ilamoGo), true);
        });
      });

      this.on(prev, 'click', () => this.go(this.current - 1, true));
      this.on(next, 'click', () => this.go(this.current + 1, true));

      this.on(slider, 'mouseenter', () => this.pauseAutoplay());
      this.on(slider, 'mouseleave', () => this.resumeAutoplay());
      this.on(slider, 'focusin', () => this.pauseAutoplay());
      this.on(slider, 'focusout', (event) => {
        if (!slider.contains(event.relatedTarget)) this.resumeAutoplay();
      });

      this.on(slider, 'keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.current - 1, true);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.current + 1, true);
        }
      });

      this.on(slider, 'pointerdown', (event) => {
        this.pointerStart = { x: event.clientX, y: event.clientY };
        this.pauseAutoplay();
      });

      this.on(slider, 'pointerup', (event) => {
        if (!this.pointerStart) return;
        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.go(this.current + (deltaX < 0 ? 1 : -1), true);
        } else {
          this.resumeAutoplay();
        }
      });

      this.on(slider, 'pointercancel', () => {
        this.pointerStart = null;
        this.resumeAutoplay();
      });
    }

    go(rawIndex, interacted = false) {
      if (this.slides.length < 2) return;
      const index = (rawIndex + this.slides.length) % this.slides.length;
      if (this.transitioning) return;
      if (index === this.current) {
        if (interacted) this.restartAutoplay();
        return;
      }

      const previous = this.slides[this.current];
      const next = this.slides[index];
      this.transitioning = true;
      this.root.classList.add('is-changing');
      previous.classList.add('is-leaving');
      next.classList.add('is-entering');
      next.setAttribute('aria-hidden', 'false');
      this.stopAutoplay();

      const commit = () => {
        void next.offsetWidth;
        next.classList.add('is-active');
        next.classList.remove('is-entering');
        this.pagination.forEach((button, buttonIndex) => {
          const active = buttonIndex === index;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', String(active));
        });
        this.current = index;
        this.playActiveVideo();

        window.clearTimeout(this.transitionTimer);
        this.transitionTimer = window.setTimeout(() => {
          previous.classList.remove('is-active', 'is-leaving');
          previous.setAttribute('aria-hidden', 'true');
          this.root.classList.remove('is-changing');
          this.transitioning = false;
          if (interacted) this.resumeAutoplay();
          else this.startAutoplay();
        }, this.reducedMotion ? 440 : 1020);
      };

      window.clearTimeout(this.commitTimer);
      this.commitTimer = window.setTimeout(commit, 40);
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.paused || document.hidden || this.transitioning) return;
      this.stopAutoplay();
      this.root.classList.remove('is-autoplaying');
      void this.root.offsetWidth;
      this.root.classList.add('is-autoplaying');
      this.timer = window.setTimeout(() => this.go(this.current + 1), this.autoplaySpeed);
    }

    stopAutoplay() {
      window.clearTimeout(this.timer);
      this.timer = null;
      this.root.classList.remove('is-autoplaying');
    }

    pauseAutoplay() {
      this.paused = true;
      this.stopAutoplay();
    }

    resumeAutoplay() {
      this.paused = false;
      this.startAutoplay();
    }

    restartAutoplay() {
      this.paused = false;
      this.startAutoplay();
    }

    prepareVideos() {
      this.root.querySelectorAll('video').forEach((video) => {
        const reveal = () => video.classList.add('is-ready');
        if (video.readyState >= 3) reveal();
        else this.on(video, 'canplay', reveal, { once: true });
      });
      this.playActiveVideo();
    }

    playActiveVideo() {
      this.slides.forEach((slide, index) => {
        slide.querySelectorAll('video').forEach((video) => {
          if (index === this.current) video.play().catch(() => {});
          else video.pause();
        });
      });
    }

    selectBlock(blockId) {
      const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (index >= 0) {
        this.pauseAutoplay();
        this.go(index);
      }
    }

    destroy() {
      this.stopAutoplay();
      window.clearTimeout(this.commitTimer);
      window.clearTimeout(this.transitionTimer);
      this.cleanup.splice(0).forEach((cleanup) => cleanup());
      this.root.dataset.initialized = 'false';
      this.root.classList.remove('ilamo-motion-ready', 'ilamo-reduced-motion', 'is-ready', 'is-changing', 'is-autoplaying');
      if (!document.querySelector(`${SELECTOR}[data-initialized="true"]`)) {
        document.body.classList.remove('ilamo-header-scrolled');
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.initialized !== 'true') root.ilamoPhaseOne = new IlamoPhaseOne(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelector(SELECTOR)?.ilamoPhaseOne?.destroy());
  




document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest(SELECTOR);
    root?.ilamoPhaseOne?.selectBlock(event.detail.blockId);
  });
})();
